import type { ActiveImpact, PresentationFrame, Vec3 } from "../core";

export const SECONDARY_FIXED_STEP = 1 / 60;
export const SECONDARY_REPLAY_WINDOW = 1.4;

export interface PresentationImpulse {
  id: string;
  /** World-space point where the authoritative contact was presented. */
  origin: Vec3;
  /** Unit world-space direction in which the visual impulse travels. */
  direction: Vec3;
  magnitude: number;
  radius: number;
  startedAt: number;
  duration: number;
  pointHeight: number;
}

export interface PresentationForceField {
  wind: Vec3;
  impulse: Vec3;
  total: Vec3;
}

export interface SecondaryResponse {
  stiffness: number;
  damping: number;
  windAffinity: number;
  impulseAffinity: number;
  /** Maximum displacement in the responder's own output units. */
  limit: number;
}

export interface SecondaryMotionSample {
  displacement: Vec3;
  velocity: Vec3;
}

export interface SecondaryMotionInput {
  position: Vec3;
  time: number;
  impulses: readonly PresentationImpulse[];
  response: SecondaryResponse;
  windStrength?: number;
}

const ZERO: Vec3 = Object.freeze({ x: 0, y: 0, z: 0 });

/**
 * One deterministic world-space force field shared by rigs and scenery.
 * The low-frequency vertical term is intentionally tiny: cloth ignores it,
 * while a surface responder can use it as inexpensive wind chop.
 */
export function samplePresentationForceField(
  position: Vec3,
  time: number,
  impulses: readonly PresentationImpulse[],
  windStrength = 1,
): PresentationForceField {
  const result: PresentationForceField = {
    wind: { ...ZERO },
    impulse: { ...ZERO },
    total: { ...ZERO },
  };
  samplePresentationForceFieldInto(position, time, impulses, windStrength, result);
  return result;
}

function samplePresentationForceFieldInto(
  position: Vec3,
  time: number,
  impulses: readonly PresentationImpulse[],
  windStrength: number,
  result: PresentationForceField,
): void {
  const phase = position.x * 0.61 + position.z * 0.37;
  const gust = 0.72 + Math.sin(time * 0.83 + phase) * 0.2 + Math.sin(time * 2.17 - phase * 0.6) * 0.08;
  result.wind.x = windStrength * gust * 0.72;
  result.wind.y = windStrength * Math.sin(time * 1.31 + phase) * 0.16;
  result.wind.z = windStrength * (0.2 + Math.sin(time * 0.57 + phase * 1.4) * 0.28);
  result.impulse.x = 0;
  result.impulse.y = 0;
  result.impulse.z = 0;

  for (const event of impulses) {
    const age = time - event.startedAt;
    if (age < 0 || age > event.duration) continue;
    const distance = Math.hypot(
      position.x - event.origin.x,
      position.y - event.origin.y,
      position.z - event.origin.z,
    );
    const spatial = Math.max(0, 1 - distance / Math.max(0.001, event.radius));
    if (spatial <= 0) continue;
    const temporal = Math.sin(Math.PI * age / Math.max(0.001, event.duration));
    const amount = Math.max(0, event.magnitude) * spatial * temporal;
    result.impulse.x += event.direction.x * amount;
    result.impulse.z += event.direction.z * amount;
    // A contact compresses the coarse surface locally even when the gameplay
    // impulse itself is planar. This remains disposable presentation motion.
    result.impulse.y += (event.direction.y - 0.32) * amount;
  }
  result.total.x = result.wind.x + result.impulse.x;
  result.total.y = result.wind.y + result.impulse.y;
  result.total.z = result.wind.z + result.impulse.z;
}

/**
 * Replays a bounded fixed-step window from rest. This costs a small, fixed
 * amount of work and makes the result independent of render FPS or seek path.
 */
export function sampleSecondaryMotion(input: SecondaryMotionInput): SecondaryMotionSample {
  const time = Math.max(0, input.time);
  const start = Math.max(0, time - SECONDARY_REPLAY_WINDOW);
  const displacement = { ...ZERO };
  const velocity = { ...ZERO };
  const response = input.response;
  // Reuse one field object for every fixed substep. A full scene evaluates
  // thousands of substeps per display frame, so this keeps GC out of motion.
  const field: PresentationForceField = {
    wind: { ...ZERO },
    impulse: { ...ZERO },
    total: { ...ZERO },
  };

  const integrate = (at: number, dt: number): void => {
    samplePresentationForceFieldInto(input.position, at, input.impulses, input.windStrength ?? 1, field);
    for (const axis of ["x", "y", "z"] as const) {
      const force = field.wind[axis] * response.windAffinity + field.impulse[axis] * response.impulseAffinity;
      const acceleration = force - displacement[axis] * response.stiffness - velocity[axis] * response.damping;
      velocity[axis] += acceleration * dt;
      displacement[axis] += velocity[axis] * dt;
      if (Math.abs(displacement[axis]) > response.limit) {
        displacement[axis] = Math.sign(displacement[axis]) * response.limit;
        velocity[axis] *= 0.2;
      }
    }
  };

  let at = start;
  while (at + SECONDARY_FIXED_STEP <= time + 1e-9) {
    at += SECONDARY_FIXED_STEP;
    integrate(at, SECONDARY_FIXED_STEP);
  }
  const remainder = time - at;
  if (remainder > 1e-9) integrate(time, remainder);

  return { displacement, velocity };
}

/** Project active and recently elapsed authoritative contacts exactly once. */
export function projectPresentationImpulses(frame: PresentationFrame): PresentationImpulse[] {
  const entities = new Map(frame.entities.map((entity) => [entity.id, entity]));
  const facts = new Map<string, { impact: ActiveImpact; startedAt: number }>();
  for (const impact of frame.impacts) {
    facts.set(impact.eventId, { impact, startedAt: frame.time - impact.age });
  }
  for (const event of frame.recentEvents) {
    if (event.type !== "impact" || facts.has(event.id)) continue;
    const age = frame.time - event.at;
    if (age < 0 || age > SECONDARY_REPLAY_WINDOW) continue;
    facts.set(event.id, {
      startedAt: event.at,
      impact: {
        eventId: event.id,
        sourceId: event.sourceId,
        targetId: event.targetId,
        outcome: event.outcome,
        amount: event.amount,
        effect: event.effect,
        motion: event.motion,
        age,
      },
    });
  }

  const projected: PresentationImpulse[] = [];
  for (const { impact, startedAt } of facts.values()) {
    if (impact.outcome === "miss" || impact.outcome === "heal") continue;
    const target = entities.get(impact.targetId);
    if (!target) continue;
    const source = entities.get(impact.sourceId);
    const fallback = source
      ? {
          x: target.position.x - source.position.x,
          y: target.position.y - source.position.y,
          z: target.position.z - source.position.z,
        }
      : { x: 0, y: 0, z: 1 };
    const amountStrength = impact.amount !== undefined && target.vitals?.maxHealth
      ? impact.amount / (target.vitals.maxHealth * 0.3)
      : 0.68;
    const pointHeight = clamp01(impact.motion?.pointHeight ?? 0.58);
    projected.push({
      id: impact.eventId,
      origin: {
        x: target.position.x,
        y: target.position.y + pointHeight * 2.7,
        z: target.position.z,
      },
      direction: normalizedImpulseDirection(impact.motion?.direction ?? fallback),
      magnitude: clamp01(impact.motion?.magnitude ?? amountStrength),
      radius: 3.4,
      startedAt,
      duration: 0.15,
      pointHeight,
    });
  }
  return projected;
}

export function normalizedImpulseDirection(direction: Vec3): Vec3 {
  const length = Math.hypot(direction.x, direction.y, direction.z);
  if (length < 1e-5) return { x: 0, y: 0, z: 1 };
  return { x: direction.x / length, y: direction.y / length, z: direction.z / length };
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}
